import type { CSSProperties, FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ResourceCategory, ResourceSummary } from '@/types'

import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { Button } from '@/components/generals/button'
import { PageHero } from '@/components/sections/page-hero'
import { ResourceCard } from '@/components/resources/resource-card'

import {
    getResourceCategories,
    getResources,
    safely
} from '@/lib/content'

interface ResourcesProps {
    resources: Array<ResourceSummary>
    categories: Array<ResourceCategory>
}

const Resources: FC<ResourcesProps> = ({ resources, categories }): ReactNode => {
    const router = useRouter()
    const [category, setCategory] = useState('all')
    const [search, setSearch] = useState('')

    useEffect(() => {
        if (!router.isReady) return

        const requested = typeof router.query.category === 'string' ? router.query.category : 'all'

        setCategory(categories.some(item => item.slug === requested) ? requested : 'all')
    }, [router.isReady, router.query.category, categories])

    const selectCategory = (slug: string) => {
        setCategory(slug)

        const query = slug === 'all' ? {} : { category: slug }

        router.replace({ pathname: '/resources', query }, undefined, { shallow: true, scroll: false })
    }

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase()

        return resources.filter(resource => {
            const inCategory = category === 'all' || resource.category?.slug === category
            const matches = !term || [resource.title, resource.excerpt ?? '', resource.category?.name ?? '']
                .some(value => value.toLowerCase().includes(term))

            return inCategory && matches
        })
    }, [resources, category, search])

    const browsing = category === 'all' && !search.trim()
    const featured = browsing ? (filtered.find(resource => resource.featured) ?? filtered[0]) : undefined
    const rest = featured ? filtered.filter(resource => resource.id !== featured.id) : filtered

    const clear = () => {
        setSearch('')
        selectCategory('all')
    }

    return (
        <Layout>
            <Seo
                title='Resources'
                description='Guides, insights and playbooks for modern finance teams: closing faster, forecasting cash, reading your P&L and putting AI to work.'
            />

            <PageHero
                eyebrow='Resources'
                title={<>Ideas for sharper <span className='text-stone'>finance teams.</span></>}
                description='Practical guides, perspectives and playbooks on closing faster, forecasting with confidence and putting automation to work.'
            />

            <section aria-label='Resource library' className='bg-cream'>
                <Container className='pb-28 md:pb-36'>
                    <div className='flex flex-col gap-5 border-b border-burgundy/10 pb-8 lg:flex-row lg:items-center lg:justify-between'>
                        <div role='group' aria-label='Filter by category' className='flex flex-wrap gap-2'>
                            {[{ slug: 'all', name: 'All' }, ...categories].map(item => (
                                <button
                                    key={item.slug}
                                    type='button'
                                    aria-pressed={category === item.slug}
                                    onClick={() => selectCategory(item.slug)}
                                    className={cn(
                                        'rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-300',
                                        category === item.slug
                                            ? 'border-burgundy bg-burgundy text-cream'
                                            : 'border-burgundy/15 text-stone-dark hover:border-burgundy/35 hover:text-burgundy'
                                    )}
                                >
                                    {item.name}
                                </button>
                            ))}
                        </div>

                        <div className='relative w-full lg:max-w-xs'>
                            <label htmlFor='resource-search' className='sr-only'>Search articles</label>
                            <Search aria-hidden='true' className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-stone' />

                            <input
                                id='resource-search'
                                type='search'
                                value={search}
                                onChange={event => setSearch(event.target.value)}
                                placeholder='Search articles'
                                className='h-11 w-full rounded-full border border-burgundy/15 bg-ivory/70 pl-11 pr-10 text-sm text-burgundy outline-none transition-colors placeholder:text-stone focus:border-burgundy/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-dark [&::-webkit-search-cancel-button]:hidden'
                            />

                            {search && (
                                <button
                                    type='button'
                                    onClick={() => setSearch('')}
                                    aria-label='Clear search'
                                    className='absolute right-3 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-stone hover:text-burgundy'
                                >
                                    <X aria-hidden='true' className='size-3.5' />
                                </button>
                            )}
                        </div>
                    </div>

                    <p aria-live='polite' className='mt-6 text-sm text-stone'>
                        {filtered.length === 1 ? '1 article' : `${filtered.length} articles`}
                    </p>

                    {featured && (
                        <div className='mt-10 border-b border-burgundy/10 pb-16'>
                            <ResourceCard resource={featured} featured />
                        </div>
                    )}

                    {rest.length > 0 && (
                        <ul className='mt-14 grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3'>
                            {rest.map((resource, index) => (
                                <li
                                    key={resource.id}
                                    data-reveal=''
                                    style={{ '--reveal-delay': `${(index % 3) * 90}ms` } as CSSProperties}
                                >
                                    <ResourceCard resource={resource} />
                                </li>
                            ))}
                        </ul>
                    )}

                    {filtered.length === 0 && (
                        <div className='mt-14 rounded-[28px] border border-dashed border-burgundy/20 bg-ivory/50 px-6 py-16 text-center'>
                            <p className='font-heading text-2xl font-medium text-burgundy'>
                                {resources.length ? 'No articles match your filters.' : 'New articles are on their way.'}
                            </p>

                            <p className='mx-auto mt-3 max-w-md text-stone-dark'>
                                {resources.length
                                    ? 'Try another category or a different search term.'
                                    : 'Our team is writing the first guides and playbooks. Check back soon.'}
                            </p>

                            {resources.length > 0 && (
                                <Button variant='secondary' className='mt-8' onClick={clear}>
                                    Clear filters
                                </Button>
                            )}
                        </div>
                    )}
                </Container>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<ResourcesProps> = async () => {
    const [resources, categories] = await Promise.all([
        safely(getResources, [], 'resources'),
        safely(getResourceCategories, [], 'resource categories')
    ])

    return {
        props: { resources, categories },
        revalidate: 60
    }
}

export default Resources
